import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { useCountryData } from '../hooks/useCountryData';
import { useScenario } from '../contexts/ScenarioContext';
import { getCountrySeries, INDICATORS, SeriesResult } from '../utils/api';
import { agentsPerCapitaFor, countryMachines, ENDPOINT_FORMULA, SCENARIO_AGENT_FORMULA } from '../utils/estimates';
import { formatCompact, formatInt, formatPct } from '../utils/format';
import { HUMANS, scenarios } from '../data/populations';
import { DataMode } from '../types/census';
import { PageLoader } from '../components/PageLoader';
import { SourceRef } from '../components/SourceRef';
import { DataBadge } from '../components/DataBadge';
import { LiveCounter } from '../components/LiveCounter';
import { TrendChart } from '../components/countries/TrendChart';
import { TopNetworks } from '../components/countries/TopNetworks';
import { AspopResult, getAspop } from '../utils/apnic';
import { speedIndex, SPEED_AS_OF } from '../data/speedIndex';
import { CountryEcosystem } from '../components/countries/CountryEcosystem';
import { PageSources } from '../components/PageSources';
export function CountryDetail() {
  const {
    code = ''
  } = useParams();
  const cca3 = code.toUpperCase();
  const data = useCountryData();
  const {
    scenario,
    scenarioId,
    setScenarioId
  } = useScenario();
  const [trend, setTrend] = useState<SeriesResult | null>(null);
  const [sex, setSex] = useState<{
    male: SeriesResult;
    female: SeriesResult;
  } | null>(null);
  const [aspop, setAspop] = useState<AspopResult | null>(null);
  const cca2 = data.rows.find(r => r.cca3 === cca3)?.cca2 ?? null;
  useEffect(() => {
    if (!cca2) return;
    let alive = true;
    setAspop(null);
    getAspop(cca2).then(r => alive && setAspop(r));
    return () => {
      alive = false;
    };
  }, [cca2]);
  useEffect(() => {
    let alive = true;
    setTrend(null);
    setSex(null);
    getCountrySeries(cca3, INDICATORS.internet).then(t => alive && setTrend(t));
    Promise.all([getCountrySeries(cca3, INDICATORS.male), getCountrySeries(cca3, INDICATORS.female)]).then(([male, female]) => alive && setSex({
      male,
      female
    }));
    return () => {
      alive = false;
    };
  }, [cca3]);
  if (data.loading) return <PageLoader label="Loading country record" data-id="element-1036" data-mp="39ferti1pe124" />;
  const row = data.rows.find(r => r.cca3 === cca3);
  if (!row) {
    return <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-8" data-id="element-1037" data-mp="2ula7rul5w95a">
        <h1 className="font-serif text-3xl text-ink" data-id="element-1038" data-mp="2zjbt6m628kjx">No record for “{code}”</h1>
        <p className="mt-3 text-muted" data-id="element-1039" data-mp="2lj11jgwxf1xp">Country pages use ISO 3166 alpha-3 codes, e.g. /countries/USA.</p>
        <Link to="/countries" className="mt-6 inline-block text-machine underline underline-offset-4" data-id="element-1040" data-mp="2qgsqq366s9y7">
          Back to all countries
        </Link>
      </div>;
  }
  const worldShare = row.population / HUMANS.base;
  const machines = countryMachines(row, scenarioId);
  const machineRate = machines != null ? scenario.ratePerSec * (machines / scenario.count) : 0;
  const male = sex?.male.points.at(-1);
  const female = sex?.female.points.at(-1);
  const malePct = male && female ? male.value / (male.value + female.value) * 100 : null;
  const speed = speedIndex[row.cca3];
  const stats: Array<{
    label: string;
    value: string;
    sub: string;
    sourceId: string;
    estimate?: boolean;
    snapshot?: boolean;
  }> = [{
    label: 'Population',
    value: formatInt(row.population),
    sub: `${formatPct(worldShare * 100, 2)} of humanity`,
    sourceId: 'restcountries'
  }, {
    label: 'Internet users',
    value: row.internetPct ? formatPct(row.internetPct.value) : '—',
    sub: row.internetUsers != null ? `${formatCompact(row.internetUsers)} people · ${row.internetPct?.year}` : 'Not reported',
    sourceId: 'wbInternet'
  }, {
    label: 'Mobile subscriptions',
    value: row.mobilePer100 ? `${row.mobilePer100.value.toFixed(1)} / 100` : '—',
    sub: row.mobilePer100 ? `World Bank ${row.mobilePer100.year}` : 'Not reported',
    sourceId: 'wbMobile'
  }, {
    label: 'Median age',
    value: row.medianAge != null ? `${row.medianAge.toFixed(1)} yrs` : '—',
    sub: 'UN WPP 2024',
    sourceId: 'unwpp'
  }, {
    label: 'Working-age share',
    value: row.workingAgePct ? formatPct(row.workingAgePct.value) : '—',
    sub: 'Ages 15–64',
    sourceId: 'wbWorking'
  }, {
    label: 'Internet speed (fixed / mobile)',
    value: speed ? `${speed.fixed} / ${speed.mobile} Mbps` : '—',
    sub: speed ? 'Median download · Ookla Speedtest Global Index' : 'Not in the Ookla top-100 snapshot — no estimate made',
    sourceId: 'ookla',
    snapshot: true
  }, {
    label: 'Sex split (male / female)',
    value: malePct != null ? `${malePct.toFixed(1)} / ${(100 - malePct).toFixed(1)}` : sex ? '—' : '…',
    sub: male ? `World Bank ${male.year}` : 'Not reported',
    sourceId: 'wbSex'
  }, {
    label: 'Assistant endpoints',
    value: row.endpoints != null ? formatCompact(row.endpoints) : '—',
    sub: ENDPOINT_FORMULA,
    sourceId: 'censusModel',
    estimate: true
  }, {
    label: `Agents per capita · ${scenario.label}`,
    value: (() => {
      const pc = agentsPerCapitaFor(row, scenarioId);
      return pc != null ? pc < 0.1 ? pc.toFixed(4) : pc.toFixed(2) : '—';
    })(),
    sub: SCENARIO_AGENT_FORMULA[scenarioId],
    sourceId: 'censusModel',
    estimate: true
  }];
  const quality: Array<{
    field: string;
    mode: DataMode | 'derived';
    detail: string;
  }> = [{
    field: 'Population, flag, capital',
    mode: data.modes.countries,
    detail: data.asOf.countries
  }, {
    field: 'Internet users',
    mode: data.modes.internet,
    detail: data.asOf.internet
  }, {
    field: 'Mobile subscriptions',
    mode: data.modes.mobile,
    detail: data.asOf.mobile
  }, {
    field: 'Working-age share',
    mode: data.modes.working,
    detail: data.asOf.working
  }, {
    field: 'Internet trend',
    mode: trend?.mode ?? 'snapshot',
    detail: trend?.asOf ?? 'loading'
  }, {
    field: 'Median age',
    mode: 'snapshot',
    detail: 'Embedded UN WPP 2024'
  }, {
    field: 'Top networks',
    mode: aspop?.mode ?? 'snapshot',
    detail: aspop ? `APNIC aspop · ${aspop.asOf}` : 'APNIC aspop · loading'
  }, {
    field: 'Speed',
    mode: 'snapshot',
    detail: `Ookla Global Index · ${SPEED_AS_OF}`
  }, {
    field: 'Endpoints & agents',
    mode: 'derived',
    detail: 'Census model on the fields above'
  }, {
    field: 'Builders, models, platforms',
    mode: 'snapshot',
    detail: 'Curated datasets · Oct 5, 2026'
  }];
  return <>
      <header className="border-b border-line" data-id="element-1041" data-mp="1n8krq90gh88a">
        <div className="mx-auto max-w-[1400px] px-5 pb-10 pt-8 md:px-8" data-id="element-1042" data-mp="1s7dub6e7feto">
          <Link to="/countries" className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-ink" data-id="element-1043" data-mp="1s8oxb3ztsijq">
            <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-1044" data-mp="1igt0rxde10hm" /> All countries
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-5" data-id="element-1045" data-mp="1ieq26oy65z1a">
            <img src={row.flag} alt={`Flag of ${row.name}`} className="h-14 w-20 rounded object-cover ring-1 ring-line" data-id="element-1046" data-mp="2652ioo7hd43h" />
            <div data-id="element-1047" data-mp="2az6o3313l42g">
              <h1 className="font-serif text-4xl text-ink md:text-5xl" data-id="element-1048" data-mp="3vniuo0azemou">{row.name}</h1>
              <p className="mt-1 font-mono text-[12px] text-muted" data-id="element-1049" data-mp="3vlfvoqrupj7z">
                {row.cca3} · {row.region} · Capital {row.capital}
              </p>
              <Link to={`/compare?c=${row.cca3}`} className="mt-2 inline-block text-[13px] text-muted underline decoration-line underline-offset-2 hover:text-machine" data-id="element-1050" data-mp="89eulai794b4">
                Compare with other countries →
              </Link>
            </div>
          </div>

          <div className="mt-10 grid gap-px overflow-hidden rounded-lg border border-line bg-line md:grid-cols-2" data-id="element-1051" data-mp="37qd5aedef7pe">
            <div className="bg-surface p-6" data-id="element-1052" data-mp="3lpux72lzr12z">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-human" data-id="element-1053" data-mp="3lni907pn3uhh">
                Humans in {row.name}
                <SourceRef id="restcountries" data-id="element-1054" data-mp="10z3ypwq7hkgy" />
              </p>
              <p className="mt-3 text-[clamp(1.8rem,3.6vw,3rem)] leading-none text-human" data-id="element-1055" data-mp="111goux64b153">
                <LiveCounter base={row.population} baseTime={HUMANS.baseTime} ratePerSec={HUMANS.ratePerSec * worldShare} label={`Humans in ${row.name}`} data-id="element-1056" data-mp="w013f4012sm6" />
              </p>
              <p className="mt-3 font-mono text-[11.5px] text-muted" data-id="element-1057" data-mp="1l2jm25cpsr66">+{(HUMANS.ratePerSec * worldShare).toFixed(3)} / sec · world rate × population share (estimate)</p>
            </div>
            <div className="bg-surface p-6" data-id="element-1058" data-mp="1tu21ge1u72y7">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-machine" data-id="element-1059" data-mp="1tvwobxwo554x">
                Machines — {scenario.label}
                <SourceRef id="censusModel" estimate={machines != null ? {
                low: formatCompact(machines * 0.8),
                high: formatCompact(machines * 1.25)
              } : undefined} data-id="element-1060" data-mp="23rq0vhae0ymp" />
              </p>
              <p className="mt-3 text-[clamp(1.8rem,3.6vw,3rem)] leading-none text-machine" data-id="element-1061" data-mp="2cso2xu19mw2n">
                {machines != null ? <LiveCounter base={machines} baseTime={HUMANS.baseTime} ratePerSec={machineRate} label={`Machines in ${row.name}`} data-id="element-1062" data-mp="edbdb0b3vhss" /> : <span className="font-mono" data-id="element-1063" data-mp="idvc08bbuth1">—</span>}
              </p>
              <div role="radiogroup" aria-label="Scenario" className="mt-3 flex flex-wrap gap-1.5" data-id="element-1064" data-mp="av47u17gojc1">
                {scenarios.map(s => <button key={s.id} type="button" role="radio" aria-checked={s.id === scenarioId} onClick={() => setScenarioId(s.id)} className={`whitespace-nowrap rounded-full border px-2.5 py-1 font-mono text-[11px] transition-colors duration-150 ${s.id === scenarioId ? 'border-machine text-ink' : 'border-line text-muted hover:text-ink'}`} data-id="element-1065" data-mp="63m455e204nd">
                    {s.label}
                  </button>)}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 pt-10 md:px-8" data-id="element-1066" data-mp="fwjprkece9q7">
        <section aria-labelledby="stats-title" data-id="element-1067" data-mp="35l3x5r2teyg7">
          <h2 id="stats-title" className="font-serif text-2xl text-ink" data-id="element-1068" data-mp="30kpn63hnlefj">
            The record
          </h2>
          <dl className="mt-5 grid border-t border-line sm:grid-cols-2 lg:grid-cols-3" data-id="element-1069" data-mp="2rejw98czr1ij">
            {stats.map(s => <div key={s.label} className="border-b border-line py-4 sm:pr-6" data-id="element-1070" data-mp="2mh1kla7la86u">
                <dt className="text-[12.5px] text-muted" data-id="element-1071" data-mp="307wv0qxqkxcj">
                  {s.label}
                  {s.estimate && <span className="ml-2 rounded border border-machine/40 px-1 py-px font-mono text-[10px] text-machine" data-id="element-1072" data-mp="2vgewe9j6r8uq">estimate</span>}
                  {s.snapshot && <span className="ml-2 rounded border border-line px-1 py-px font-mono text-[10px] text-muted" data-id="element-1073" data-mp="3a7nu75x8s0iy">snapshot</span>}
                </dt>
                <dd className={`mt-1 font-mono text-xl tabular ${s.estimate ? 'text-machine' : 'text-ink'}`} data-id="element-1074" data-mp="3j6iz8eojv4ko">
                  {s.value}
                  <SourceRef id={s.sourceId} estimate={s.estimate ? {
                low: '−15%',
                high: '+15%'
              } : undefined} data-id="element-1075" data-mp="3f5fjrtaezajz" />
                </dd>
                <dd className="mt-0.5 break-words font-mono text-[11px] text-faint" data-id="element-1076" data-mp="1kqmsg6gkd5xu">{s.sub}</dd>
              </div>)}
          </dl>

          <div className="mt-12" data-id="element-1077" data-mp="1anzjmthm0b2k">
            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1078" data-mp="1aossq141jcnv">
              <h2 className="font-serif text-2xl text-ink" data-id="element-1079" data-mp="1enrsl7ax0j8g">
                Internet users, % of population
                <SourceRef id="wbInternet" data-id="element-1080" data-mp="10wmui9n73z08" />
              </h2>
            </div>
            {trend && <PageSources data-id="element-1081" data-mp="15tvb45sp3uzd">
                <DataBadge mode={trend.mode} source="World Bank · internet trend" asOf={trend.asOf} data-id="element-1082" data-mp="qyyvh7ik45c9" />
              </PageSources>}
            <p className="mt-1 text-[13px] text-muted" data-id="element-1083" data-mp="vylczt5f47qv">Last five reported years.</p>
            <div className="mt-4 rounded-lg border border-line bg-surface p-4" data-id="element-1084" data-mp="hz2cnmium0p4">
              {trend ? <TrendChart points={trend.points} label={`${row.name} internet users`} data-id="element-1085" data-mp="mwc1p12bsacf" /> : <p className="py-10 text-center font-mono text-xs text-muted" data-id="element-1086" data-mp="2ccbeqt92ejxc">Fetching series…</p>}
            </div>
          </div>

          <TopNetworks cca2={row.cca2} countryName={row.name} population={row.population} result={aspop} data-id="element-1087" data-mp="2ha3hkib8zqgb" />
          <CountryEcosystem cca3={row.cca3} countryName={row.name} data-id="element-1088" data-mp="2ex9f2dz3oc58" />
        </section>

        <section aria-labelledby="quality-title" className="mt-14 border-t border-line pt-8" data-id="element-1089" data-mp="2aumrwqddjfed">
          <h2 id="quality-title" className="text-[15px] font-medium text-ink" data-id="element-1090" data-mp="25v06vurb0uv0">
            Data quality
          </h2>
          <p className="mt-1 text-[13px] text-muted" data-id="element-1091" data-mp="20y09s9cfn2g1">Which fields on this page are live and which are dated snapshots.</p>
          <ul className="mt-4 grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1092" data-mp="2yjnu3vvm5ub7">
            {quality.map(q => <li key={q.field} className="flex items-start justify-between gap-4 border-b border-line/60 py-2.5" data-id="element-1093" data-mp="2tnqfbu000w0j">
                <div className="min-w-0" data-id="element-1094" data-mp="2jvkoinlhgkmx">
                  <p className="text-[13px] text-ink" data-id="element-1095" data-mp="2jurqcb0lb3ge">{q.field}</p>
                  <p className="truncate font-mono text-[10.5px] text-faint" data-id="element-1096" data-mp="3haow6ss165yz">{q.detail}</p>
                </div>
                <span className={`shrink-0 font-mono text-[11px] ${q.mode === 'live' ? 'text-live' : q.mode === 'derived' ? 'text-muted' : 'text-machine'}`} data-id="element-1097" data-mp="3cjynb5kebkzj">{q.mode}</span>
              </li>)}
          </ul>
        </section>
      </div>
    </>;
}